"use client";
import React, { createContext, useContext, useRef, useState } from "react";
import type { SelectProps as AriaSelectProps, Key, ValidationResult } from "react-aria-components";
import { Select as AriaSelect, ListBox, SelectValue } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { composeTailwindRenderProps } from "../../utils";
import type { Size } from "../../utils/controlStyles";
import { controlStyles } from "../../utils/controlStyles";
import { Button } from "../Button";
import { Description, FieldError, getInputBaseStyles, InputWrapper, Label } from "../Field";
import { Icon } from "../Icon";
import { ListBoxItem } from "../ListBoxItem";
import { Popover } from "../Popover";
import { Tray } from "../Tray";

// Context to provide size to nested ListBoxItems
const SelectSizeContext = createContext<Size>("md");

/**
 * Hook to access the current Select size from context.
 * Used by ListBoxItem to automatically inherit the parent Select's size.
 */
export function useSelectSize(): Size {
  return useContext(SelectSizeContext);
}

function getSelectValueClassName(size: Size = "md") {
  // Use controlStyles for consistent text sizing
  // Apply the same mobile font override as TextField to prevent auto-zoom
  const mobileFontClass =
    size === "sm"
      ? "!text-base md:!text-[length:var(--control-text-sm)]"
      : size === "md"
        ? "!text-base md:!text-[length:var(--control-text-md)]"
        : "";

  return `flex-1 text-start data-[placeholder]:text-text-disabled overflow-hidden text-ellipsis whitespace-nowrap ${controlStyles[size].text} ${mobileFontClass}`;
}

// Define a custom hook for focus handling that works with any element
function useElementFocus() {
  const handleFocus = React.useCallback(
    (e: React.FocusEvent<Element>, onFocus?: (e: React.FocusEvent<Element>) => void) => {
      onFocus?.(e);
    },
    []
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<Element>, onBlur?: (e: React.FocusEvent<Element>) => void) => {
      onBlur?.(e);
    },
    []
  );

  return { handleFocus, handleBlur };
}

/**
 * Interface defining the shape of items in the Select component
 */
export interface SelectItem<T = unknown> {
  /** Unique identifier for the item */
  id: string;
  /** Display label for the item */
  label: string;
  /** Value associated with the item */
  value: T;
}

/**
 * Props for the Select component
 * @template T - The type of the items, must extend SelectItem
 */
export interface SelectProps<T extends SelectItem> extends Omit<AriaSelectProps<T>, "children" | "onSelectionChange"> {
  /** Label text displayed above the select */
  label?: string;
  /** Helper text displayed below the select */
  description?: string;
  /** Error message or function to generate error message */
  errorMessage?: string | ((validation: ValidationResult) => React.ReactNode);
  /** Collection of items to display in the select */
  items?: Iterable<T>;
  /** Size variant of the select */
  size?: Size;
  /** Render function for items or static children */
  children?: React.ReactNode | ((item: T) => React.ReactNode);
  /** Custom render function for individual items. When provided, items prop is used to render items automatically */
  renderItem?: (item: T) => React.ReactNode;
  /** Custom render function for the selected value display. When provided, this overrides the default SelectValue display */
  renderSelectedValue?: (item: T) => React.ReactNode;
  /** Currently selected item's id. Uses the SelectItem's `id` field for stable selection tracking */
  selectedKey?: Key;
  /** Default selected item's id for uncontrolled usage */
  defaultSelectedKey?: Key;
  /** Callback when selection changes, provides the selected item's id */
  onSelectionChange?: (key: Key) => void;
  /** Whether to show error states */
  showErrors?: boolean;
  /** Tooltip text */
  tooltip?: string;
  /** Whether the field is required */
  isRequired?: boolean;
  /** Validation result object */
  validationResult?: ValidationResult;
  /** Whether to use transparent background */
  transparent?: boolean;
  /**
   * Whether to reserve space for error messages to prevent layout shift
   * @default false
   */
  reserveErrorSpace?: boolean;
  /** Placement of the dropdown menu relative to the trigger. Defaults to "bottom start" */
  placement?:
    | "bottom"
    | "bottom left"
    | "bottom right"
    | "bottom start"
    | "bottom end"
    | "top"
    | "top left"
    | "top right"
    | "top start"
    | "top end"
    | "left"
    | "left top"
    | "left bottom"
    | "start"
    | "start top"
    | "start bottom"
    | "right"
    | "right top"
    | "right bottom"
    | "end"
    | "end top"
    | "end bottom";
  /** Whether the menu should flip to the opposite side when there's not enough space. Defaults to true */
  shouldFlip?: boolean;
  /**
   * Controls the menu width behavior relative to the trigger.
   * - "match": Menu width matches trigger width exactly (default)
   * - "auto": Menu width grows to fit content, with minimum of trigger width
   * @default "match"
   */
  menuWidth?: "match" | "auto";
  /**
   * Where to display the description text
   * - "below": Show below the field (default)
   * - "tooltip": Show as a tooltip on the label icon
   * - "inline": Show inline with the label
   * - "hidden": Don't display the description
   * @default "below"
   */
  descriptionPlacement?: "below" | "tooltip" | "inline" | "hidden";
  /**
   * Whether to use a Tray (bottom sheet) on mobile devices instead of a Popover
   * @default true
   */
  useMobileTray?: boolean;
}

export function Select<T extends SelectItem>({
  label,
  description,
  errorMessage,
  children,
  items,
  renderItem,
  renderSelectedValue,
  size = "md",
  selectedKey: controlledSelectedKey,
  defaultSelectedKey,
  onSelectionChange,
  placeholder,
  showErrors = false,
  tooltip,
  isRequired,
  transparent,
  reserveErrorSpace = false,
  placement = "bottom start",
  shouldFlip = true,
  menuWidth = "match",
  descriptionPlacement = "below",
  useMobileTray = true,
  ...props
}: SelectProps<T>) {
  const [internalSelectedKey, setInternalSelectedKey] = useState<Key | null>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);
  const [isTrayOpen, setIsTrayOpen] = useState(false);
  const selectedKey = controlledSelectedKey ?? internalSelectedKey ?? defaultSelectedKey;
  const { handleFocus, handleBlur } = useElementFocus();
  const { isMobile } = useBreakpoint();
  const shouldUseTray = useMobileTray && isMobile;

  // Find the selected item if renderSelectedValue is provided
  const displaySelectedItem = React.useMemo(() => {
    if (!renderSelectedValue || !selectedKey || !items) return null;
    const itemsArray = Array.from(items);
    return itemsArray.find((item) => item.id === selectedKey) ?? null;
  }, [renderSelectedValue, selectedKey, items]);

  const handleSelectionChange = (key: Key | null) => {
    if (onSelectionChange && key !== null) {
      onSelectionChange(key);
    }
    if (!onSelectionChange) {
      setInternalSelectedKey(key); // Update internal state if uncontrolled
    }
  };

  const selectRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Handler for mobile tray open/close
  const handleMobileTrayToggle = () => {
    if (shouldUseTray) {
      setIsTrayOpen(!isTrayOpen);
    }
  };

  return (
    <>
      <AriaSelect
        {...props}
        selectedKey={selectedKey}
        defaultSelectedKey={defaultSelectedKey}
        onSelectionChange={handleSelectionChange}
        placeholder={placeholder}
        className={composeTailwindRenderProps(props.className, "group flex w-full flex-col")}
        ref={selectRef}
        onOpenChange={(isOpen) => {
          if (shouldUseTray) {
            // On mobile with tray, we don't use the built-in open state
            return;
          }
          if (isOpen && triggerRef.current) {
            setTriggerWidth(triggerRef.current.offsetWidth);
          } else if (!isOpen) {
            selectRef.current?.blur();
          }
        }}
      >
        {({ isDisabled, isInvalid, isOpen }: { isDisabled: boolean; isInvalid: boolean; isOpen: boolean }) => (
          <div className="flex flex-col">
            {label && (
              <Label
                size={size}
                tooltip={tooltip}
                description={
                  descriptionPlacement === "tooltip" || descriptionPlacement === "inline" ? description : undefined
                }
                descriptionPlacement={descriptionPlacement}
                isRequired={isRequired}
              >
                {label}
              </Label>
            )}
            <InputWrapper>
              <div ref={triggerRef}>
                <Button
                  variant="unstyled"
                  className={twMerge(
                    getInputBaseStyles({
                      isInvalid: isInvalid || (showErrors && !!errorMessage),
                      isDisabled,
                      isFocused: shouldUseTray ? isTrayOpen : isOpen,
                      transparent,
                      size,
                    }),
                    // Keep left padding consistent across sizes
                    "flex items-center text-start gap-4 cursor-pointer pl-[var(--control-padding-sm)]"
                  )}
                  onFocus={(e) => handleFocus(e, props.onFocus)}
                  onBlur={(e) => handleBlur(e, props.onBlur)}
                  onPress={shouldUseTray ? handleMobileTrayToggle : undefined}
                  data-open={shouldUseTray ? isTrayOpen : isOpen}
                >
                  {renderSelectedValue && displaySelectedItem ? (
                    <div className={getSelectValueClassName(size)}>{renderSelectedValue(displaySelectedItem)}</div>
                  ) : shouldUseTray && selectedKey ? (
                    // On mobile tray, manually render the selected value since SelectValue doesn't work with tray
                    <div className={getSelectValueClassName(size)}>
                      {(() => {
                        if (items) {
                          const itemsArray = Array.from(items);
                          const selected = itemsArray.find((item) => item.id === selectedKey);
                          return selected?.label;
                        }
                        // When using children pattern, we need to extract the text from the selected child
                        // This is a fallback that should work for simple text children
                        return String(selectedKey);
                      })()}
                    </div>
                  ) : (
                    <SelectValue className={getSelectValueClassName(size)} />
                  )}
                  <Icon
                    name="CaretDown"
                    aria-hidden
                    className={twMerge(
                      "h-4 w-4 text-text-primary group-disabled:text-text-disabled forced-colors:text-[ButtonText] transition-transform",
                      (shouldUseTray ? isTrayOpen : isOpen) && "rotate-180"
                    )}
                  />
                </Button>
              </div>
            </InputWrapper>
            {reserveErrorSpace ? (
              <div className="mt-1 min-h-[20px]">
                {showErrors ? (
                  <FieldError size={size} className="text-feedback-error-text">
                    {typeof errorMessage === "function" && props.validationResult
                      ? errorMessage(props.validationResult)
                      : typeof errorMessage === "string"
                        ? errorMessage
                        : null}
                  </FieldError>
                ) : description && descriptionPlacement === "below" ? (
                  <Description size={size}>{description}</Description>
                ) : null}
              </div>
            ) : (
              <>
                {showErrors ? (
                  <FieldError size={size} className="mt-1 text-feedback-error-text">
                    {typeof errorMessage === "function" && props.validationResult
                      ? errorMessage(props.validationResult)
                      : typeof errorMessage === "string"
                        ? errorMessage
                        : null}
                  </FieldError>
                ) : description && descriptionPlacement === "below" ? (
                  <Description size={size} className="mt-1">
                    {description}
                  </Description>
                ) : null}
              </>
            )}
            {!shouldUseTray ? (
              <Popover
                className="bg-background-input shadow-xl border border-border-input/20 backdrop-blur-sm rounded-[var(--control-border-radius)]"
                style={{
                  width: menuWidth === "match" && triggerWidth ? `${triggerWidth}px` : undefined,
                  minWidth: menuWidth === "auto" && triggerWidth ? `${triggerWidth}px` : undefined,
                  maxWidth: menuWidth === "auto" ? "min(600px, 90vw)" : undefined,
                }}
                placement={placement}
                shouldFlip={shouldFlip}
              >
                {/* Always use 'md' size for menu items - they're a separate UI surface */}
                <SelectSizeContext.Provider value="md">
                  <ListBox
                    items={items}
                    className="w-full max-h-[300px] overflow-auto bg-background-input p-0 outline-0"
                  >
                    {renderItem && items
                      ? (item: T) => (
                          <ListBoxItem key={item.id} id={item.id} textValue={item.label}>
                            <span className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1">
                              {renderItem(item)}
                            </span>
                          </ListBoxItem>
                        )
                      : children}
                  </ListBox>
                </SelectSizeContext.Provider>
              </Popover>
            ) : null}
          </div>
        )}
      </AriaSelect>
      {shouldUseTray && (
        <Tray
          isOpen={isTrayOpen}
          onClose={() => setIsTrayOpen(false)}
          title={label ?? "Select an option"}
          contentPadding={false}
        >
          {/* Always use 'md' size in tray for better readability on mobile */}
          <SelectSizeContext.Provider value="md">
            <ListBox
              items={items}
              className="w-full bg-background-surface p-0 outline-0 [&_[role=option]]:border-b [&_[role=option]]:border-border-default [&_[role=option]:last-child]:border-0 [&_[role=option]]:!px-6 [&_[role=option]]:!py-4 [&_[role=option]]:rounded-none"
              selectionMode="single"
              selectedKeys={selectedKey !== null && selectedKey !== undefined ? [selectedKey] : []}
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0];
                if (key !== undefined && key !== null) {
                  handleSelectionChange(key);
                  setIsTrayOpen(false);
                }
              }}
            >
              {renderItem && items
                ? (item: T) => (
                    <ListBoxItem key={item.id} id={item.id} textValue={item.label}>
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1">
                        {renderItem(item)}
                      </span>
                    </ListBoxItem>
                  )
                : children}
            </ListBox>
          </SelectSizeContext.Provider>
        </Tray>
      )}
    </>
  );
}
